import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Home, LogOut, UserCircle } from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { NotificationBell } from "./NotificationBell";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { language, setLanguage, t } = useLanguage();

  // Hide the Home button on the forced-password-change screen — the user must
  // finish setting a new password before navigating away.
  const isForcedChange =
    (location.pathname === "/profile" && searchParams.get("force") === "1") ||
    (user && user.must_change_password === true);

  const handleLang = async (lang) => {
    if (lang === language) return;
    setLanguage(lang);
    if (user) {
      try {
        await api.patch("/auth/me/language", { language: lang });
      } catch {
        /* ignore */
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    toast.success(t("nav_logout"));
    navigate("/login", { replace: true });
  };

  const handleHome = () => {
    const role = user?.role;
    const home = role === "admin" ? "/admin" : role === "reviewer" ? "/reviewer" : "/agent";
    navigate(home);
  };

  return (
    <header
      className="bg-white border-b border-slate-200 h-16 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40"
      data-testid="top-bar"
    >
      <div className="flex items-center">
        {user && !isForcedChange ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleHome}
            data-testid="home-link"
            className="text-slate-600 hover:text-[#0A2540]"
          >
            <Home className="h-4 w-4 mr-1" />
            {t("home")}
          </Button>
        ) : null}
      </div>

      <div className="flex items-center gap-1 sm:gap-3">
        <div
          className="flex items-center rounded-md border border-slate-200 overflow-hidden text-sm"
          data-testid="language-toggle"
        >
          <button
            type="button"
            onClick={() => handleLang("en")}
            className={`px-3 py-1.5 transition-colors focus-ring ${
              language === "en"
                ? "bg-[#0A2540] text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="language-toggle-en"
          >
            EN
          </button>
          <button
            type="button"
            onClick={() => handleLang("zh")}
            className={`px-3 py-1.5 transition-colors focus-ring ${
              language === "zh"
                ? "bg-[#0A2540] text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="language-toggle-zh"
          >
            中文
          </button>
        </div>

        {user ? (
          <>
            <NotificationBell />
            <div className="hidden sm:flex flex-col text-right leading-tight">
              <span className="text-sm font-medium text-[#0A2540]" data-testid="top-bar-user-name">
                {user.name}
              </span>
              <span className="text-xs text-slate-500">{user.email}</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/profile")}
              data-testid="profile-link"
              className="text-slate-600 hover:text-[#0A2540]"
            >
              <UserCircle className="h-4 w-4 mr-1" />
              {t("profile")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-slate-600 hover:text-[#0A2540]"
            >
              <LogOut className="h-4 w-4 mr-1" />
              {t("nav_logout")}
            </Button>
          </>
        ) : null}
      </div>
    </header>
  );
};

export default TopBar;
